/**
 * Whitelabel skins — a "skin" is a complete, swappable design system for the whole
 * platform shell (and, via the same tokens, the modules). One `ThemeConfig` expands to
 * every shell CSS variable (globals.css `:root`) plus structural flags (header layout,
 * glass, backgrounds). Swapping the active skin restyles the entire product.
 *
 * These are authored as a small set of SEMANTIC choices; `buildSkinCss` (build-skin-css.ts)
 * expands them into the full var set. Adding a skin = adding one entry here. The AI
 * system-designer (Phase 4) ultimately writes/edits these objects.
 */

export type SkinMode = "light" | "dark";
export type HeaderLayout = "horizontal" | "vertical";
export type Density = "comfortable" | "compact";

export interface ThemeConfig {
  id: string;
  name: string;
  /** One-line description shown in the skin picker. */
  blurb: string;
  mode: SkinMode;

  // ---- core palette (any CSS color string: hex, oklch, rgb) --------------
  bg: string;          // page background base color
  surface: string;     // cards / sidebar / popovers
  surface2: string;    // secondary surfaces (muted rows, hover)
  fg: string;          // primary text
  muted: string;       // secondary text
  border: string;      // hairlines / dividers
  /**
   * The border of form CONTROLS, when it must differ from `border` above.
   *
   * Defaults to `border`, which is right for every opaque skin: one hairline
   * colour for dividers and inputs alike. It is wrong for a GLASS skin, where
   * `border` is deliberately a white rim — correct on a translucent card
   * floating over a coloured gradient, invisible on an input sitting ON that
   * near-white card. Aurora Glass rendered every input in the app as
   * `1px solid rgba(255,255,255,0.65)` with a transparent fill, so the rate
   * and plan fields on the brand console read as static text rather than as
   * something you can type in.
   */
  inputBorder?: string;
  primary: string;     // brand / primary actions
  primaryFg: string;   // text on primary
  ring: string;        // focus ring
  danger?: string;

  // ---- sidebar (defaults derive from surface/fg if omitted) --------------
  sidebarBg?: string;
  sidebarFg?: string;

  // ---- shape + type -------------------------------------------------------
  radius: string;      // e.g. "0.625rem" | "2px" | "16px"
  font: string;        // CSS font-family stack for UI
  headingFont?: string;

  // ---- structure ----------------------------------------------------------
  headerLayout: HeaderLayout;
  density: Density;
  /** §3.7: sidebar/rail width in the vertical-rail layout. Optional — the
   *  house 16rem is a considered default, not a placeholder; most brands have
   *  no reason to move it. A brand with a wide wordmark or a dense nav does. */
  railWidth?: string;
  /** §3.7: caps the content area's width so it does not stretch edge to edge
   *  on a wide monitor. Most pages already set their own max-w-* — this only
   *  binds the pages that don't, e.g. full-width tables/dashboards. Unset =
   *  no cap, the house default. */
  contentMaxWidth?: string;
  /** §3.7: whether a fresh visit (no sidebar_state cookie yet) starts with the
   *  sidebar collapsed. Only applies before a user has ever toggled it — once
   *  they have, their own choice persists (the sidebar_state cookie) and this
   *  no longer matters for them. Unset = expanded, the house default. */
  sidebarCollapsedDefault?: boolean;

  // ---- effects + backgrounds ---------------------------------------------
  /** Which chrome treatment the brand wears. "designed" = the full rail/topbar/
   *  pill styling, rendered in this brand's own tokens; "plain" = generated
   *  tokens only. Custom whitelabel brands default to "designed" — before this
   *  they silently got nothing. */
  /** The shell's LAYOUT — the component tree, not a stylesheet.
   *
   *  "rail" (default) = the house information architecture: a left rail with the
   *  process taxonomy, a topbar, and the content inset beside it.
   *
   *  "app" = no rail at all. A slim brand header over a full-bleed content area,
   *  for a brand whose product IS one module (see Hearth's audience editions).
   *  A theme can restyle a rail; it cannot remove one, and while the rail is
   *  there the product reads as the house ERP however it is painted — which is
   *  the whole reason this field exists rather than another CSS variant. */
  shell?: ShellId;

  /** Which SHELL the brand wears — structure, not palette.
   *  "designed" = the premium look in this brand's tokens.
   *  "flat"     = a genuinely different shell (bordered/square/dense) for a
   *               brand that must not be recognisable as the house product.
   *  "plain"    = generated tokens only, no chrome. */
  chrome?: ChromeId | "plain";
  // ---- SHAPE (docs/agent-runtime-and-design-ai.md §3.3) -------------------
  //
  // Why these exist: before them a "redesign" could only be a recolour plus a
  // corner radius, because there was no way to say that a brand's buttons are
  // small uppercase rectangles or that its fields are underlines. So every
  // design the AI produced was house-SHAPED in a new palette, which is the
  // mechanism behind "it still looks like the original".
  //
  // All optional, all defaulting to the house values, so an edition that never
  // sets one renders exactly as it does today.

  /** Buttons. `case: "upper"` with tracking is a whole register — it is the
   *  single loudest difference between one brand's controls and another's. */
  control?: {
    radius?: string;
    case?: "none" | "upper";
    tracking?: string;
    weight?: string;
    /** A coloured glow under the primary action. */
    shadow?: boolean;
  };
  /** Form fields. `underline` is not a decoration: a field with only a bottom
   *  rule has no second corner radius to disagree with the buttons, which
   *  removes a whole class of mismatch rather than tuning it. */
  field?: { style?: "box" | "underline"; radius?: string; fill?: "solid" | "transparent" };
  /** Panels/cards. `glass` is reachable via `effects` too; this exists so a
   *  brand can say explicitly NOT glass, which a boolean flag cannot. */
  panel?: { fill?: "flat" | "gradient" | "glass"; border?: string; radius?: string };
  /** Headings. `accentClause` puts a heading's closing clause in the brand
   *  hue, the way many brand heroes do and ours never did. */
  heading?: { weight?: string; tracking?: string; accentClause?: boolean };
  /** Small-caps labels — a register of their own. */
  eyebrow?: { size?: string; tracking?: string; case?: "none" | "upper" };

  /** Semantic hues. Optional: a brand that does not care keeps the house values,
   *  a brand whose own colour IS green needs to move `success` so two greens do
   *  not end up meaning different things on the same screen. */
  gold?: string;
  success?: string;
  warning?: string;
  /** Stylesheet that provides `font` / `headingFont` (a Google Fonts or self-hosted
   *  href). Naming a family that nothing loads silently falls back to the stack's
   *  next entry, which is the failure mode this field exists to prevent. */
  fontUrl?: string;
  glass?: boolean;     // translucent surfaces + backdrop-blur
  /** Reveal `appBg` through the content area (transparent SidebarInset) with airy
   *  solid cards floating over it — e.g. a nature scene. (glass implies reveal.) */
  revealBg?: boolean;
  /** CSS `background` for the whole app shell (gradient / image). */
  appBg?: string;
  /** CSS `background` painted behind CRM kanban / board areas specifically. */
  boardBg?: string;
  /** Optional chart series colors. */
  charts?: string[];
}

/**
 * The two STRUCTURAL choices a brand makes, as pickable options.
 *
 * Deliberately named for what a person sees, not for the CSS/React mechanism:
 * a reseller choosing how their product looks should not have to know the words
 * "chrome" and "shell". The ids stay technical because they are what
 * ThemeConfig persists and what buildSkinCss switches on.
 */
export const CHROMES = [
  {
    id: "designed" as const,
    name: "Signature",
    blurb: "Rounded cards, soft shadows, frosted surfaces. Polished — suits a product people browse.",
    feel: "Polished · airy",
  },
  {
    id: "flat" as const,
    name: "Console",
    blurb: "Square corners, hairline borders, no shadows. Dense and tabular — reads like an operations tool.",
    feel: "Sharp · dense",
  },
  {
    id: "minimal" as const,
    name: "Quiet",
    blurb: "No borders, no fills. Separation by whitespace and weight alone, with a lot of air.",
    feel: "Calm · spacious",
  },
];

export const SHELLS = [
  {
    id: "rail" as const,
    name: "Workspace",
    blurb: "A left sidebar listing every app the customer has, grouped by process. Right when they use several.",
    feel: "Many apps",
  },
  {
    id: "app" as const,
    name: "Single app",
    blurb: "No sidebar. A slim branded header and the app fills the screen, with its own sections as the navigation.",
    feel: "One app",
  },
];

/**
 * Type pairings a reseller can PICK, instead of typing a CSS font stack and a
 * Google Fonts URL by hand — which is what building CREWLINE actually took.
 *
 * Each entry is self-contained: the body stack, the heading stack, and the one
 * stylesheet that provides both. The console emits all three together, so a
 * brand can never end up naming a family whose face never loads (the failure
 * mode is silent — it just falls back to system sans and looks untouched).
 *
 * Deliberately short. Twenty options is a font menu; six is a choice, and every
 * one of these is a genuinely different voice.
 */
export const FONT_PAIRINGS = [
  {
    id: "system",
    name: "System",
    feel: "Neutral · fastest",
    body: "ui-sans-serif, system-ui, -apple-system, \"Segoe UI\", Roboto, sans-serif",
    heading: "",
    url: "",
  },
  {
    id: "inter",
    name: "Inter",
    feel: "Clean · the default",
    body: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    heading: "",
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  },
  {
    id: "jakarta-fraunces",
    name: "Jakarta + Fraunces",
    feel: "Warm · editorial headings",
    body: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    heading: '"Fraunces", ui-serif, Georgia, serif',
    url: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
  },
  {
    id: "source-serif",
    name: "Source Sans + Source Serif",
    feel: "Institutional · readable",
    body: '"Source Sans 3", ui-sans-serif, system-ui, sans-serif',
    heading: '"Source Serif 4", ui-serif, Georgia, serif',
    url: "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&family=Source+Serif+4:wght@500;700&display=swap",
  },
  {
    id: "space",
    name: "Space Grotesk",
    feel: "Technical · geometric",
    body: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
    heading: "",
    url: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  },
  {
    id: "ibm-plex",
    name: "IBM Plex Sans + Mono",
    feel: "Operational · tabular",
    body: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
    heading: '"IBM Plex Mono", ui-monospace, SFMono-Regular, monospace',
    url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap",
  },
] as const;

/** Corner radius, named for what it looks like rather than in pixels. */
export const RADIUS_STEPS = [
  { id: "0px", name: "Square" },
  { id: "4px", name: "Slight" },
  { id: "10px", name: "Rounded" },
  { id: "18px", name: "Soft" },
] as const;

/**
 * The palette roles worth putting in front of a human, in the order they
 * matter. The other ThemeConfig colours (surface2, danger, gold, success,
 * warning, boardBg) stay behind "More colours" — they are corrections, not
 * decisions, and leading with fifteen swatches is how a colour picker stops
 * being easy.
 */
export const PALETTE_ROLES = [
  { key: "primary", name: "Brand", hint: "Buttons, links, anything the customer should click." },
  { key: "ring", name: "Accent", hint: "Focus rings and highlights. A second colour, used sparingly." },
  { key: "bg", name: "Page", hint: "The background behind everything." },
  { key: "surface", name: "Cards", hint: "Panels and cards sitting on the page." },
  { key: "fg", name: "Text", hint: "Body text. Must stay readable on Page and Cards." },
  { key: "border", name: "Lines", hint: "Borders, dividers and table rules." },
  { key: "sidebarBg", name: "Sidebar", hint: "Only visible with the Workspace navigation." },
  { key: "sidebarFg", name: "Sidebar text", hint: "Must stay readable on the sidebar colour." },
] as const;

export const ADVANCED_PALETTE_ROLES = [
  { key: "surface2", name: "Raised", hint: "Secondary surfaces — table headers, inset panels." },
  { key: "muted", name: "Muted text", hint: "Captions, hints, secondary labels." },
  { key: "primaryFg", name: "On brand", hint: "Text sitting on the brand colour." },
  { key: "danger", name: "Danger", hint: "Destructive actions and errors." },
  { key: "success", name: "Success", hint: "Confirmations. Move it if your brand hue is green." },
  { key: "warning", name: "Warning", hint: "Caution states." },
  { key: "gold", name: "Highlight", hint: "Accents in charts and badges." },
] as const;

export type ChromeId = (typeof CHROMES)[number]["id"];
export type ShellId = (typeof SHELLS)[number]["id"];

const INTER = 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** The platform's current look — the identity skin (no overrides beyond defaults). */
export const DEFAULT_SKIN: ThemeConfig = {
  id: "default",
  name: "QOD Classic",
  blurb: "The standard QOD look — navy, sky, and gold on white.",
  mode: "light",
  bg: "#ffffff",
  surface: "#ffffff",
  surface2: "#f4f6f8",
  fg: "#1a1a1a",
  muted: "#6b7280",
  border: "#e5e7eb",
  primary: "#1f4e79",
  primaryFg: "#ffffff",
  ring: "#1ba1e2",
  radius: "0.625rem",
  font: INTER,
  headerLayout: "horizontal",
  density: "comfortable",
  charts: ["#1f4e79", "#1ba1e2", "#c9a24b", "#2fa36b", "#7c5cd6"],
};

/**
 * 1. GLASSMORPHISM — light, airy, translucent frosted surfaces over an aurora gradient.
 * Vivid violet primary, generous radius, soft depth.
 */
export const GLASS_SKIN: ThemeConfig = {
  id: "glass",
  name: "Aurora Glass",
  blurb: "Frosted translucent surfaces floating over a soft aurora gradient.",
  mode: "light",
  bg: "#eef1ff",
  surface: "rgba(255,255,255,0.55)",
  surface2: "rgba(255,255,255,0.38)",
  fg: "#1b1a2e",
  muted: "#5b5a78",
  border: "rgba(255,255,255,0.65)",
  // A white rim reads as an edge on a translucent card over the aurora
  // gradient; on an input sitting ON that near-white card it is invisible.
  // Ink at low alpha is the same hairline weight and actually has contrast.
  inputBorder: "rgba(27,26,46,0.22)",
  primary: "#6d5efc",
  primaryFg: "#ffffff",
  ring: "#8b7cff",
  sidebarBg: "rgba(255,255,255,0.45)",
  sidebarFg: "#26243f",
  radius: "18px",
  font: INTER,
  headerLayout: "horizontal",
  density: "comfortable",
  glass: true,
  appBg:
    "radial-gradient(1200px 800px at 8% -6%, #dcd6ff 0%, transparent 55%)," +
    "radial-gradient(1100px 760px at 100% 0%, #cfeaff 0%, transparent 50%)," +
    "radial-gradient(900px 900px at 60% 120%, #ffd9f2 0%, transparent 45%)," +
    "linear-gradient(135deg, #eef1ff 0%, #f3edff 100%)",
  boardBg:
    "radial-gradient(700px 500px at 10% 0%, rgba(139,124,255,0.16), transparent 60%)," +
    "radial-gradient(700px 500px at 100% 100%, rgba(80,190,255,0.16), transparent 60%)",
  charts: ["#6d5efc", "#4cc4ff", "#c86dff", "#4bd6a8", "#ffb454"],
};

/**
 * 2. HANDYMAN / HOME-APPLIANCE ERP — sturdy, utilitarian, high-contrast. Safety-amber
 * primary, warm off-white canvas, dark charcoal tool-belt sidebar, square-ish corners.
 */
export const HANDYMAN_SKIN: ThemeConfig = {
  id: "handyman",
  name: "Toolbelt",
  blurb: "Sturdy amber-and-charcoal workwear look for field-service teams.",
  mode: "light",
  bg: "#f7f4ef",
  surface: "#ffffff",
  surface2: "#efe9e0",
  fg: "#20242b",
  muted: "#6a7078",
  border: "#e0d8cc",
  primary: "#ef6c00",
  // Dark ink, not white: white on this fill measures 3.08:1 and a 14px button
  // label needs 4.5:1. Keeps the brand hue and reaches 5.65:1 — caught by the
  // brand contrast validator, which the shipped skins have to pass too.
  primaryFg: "#1a1a1a",
  ring: "#ef6c00",
  sidebarBg: "#1f2733",
  sidebarFg: "#e8edf3",
  radius: "4px",
  font: INTER,
  headerLayout: "horizontal",
  density: "compact",
  charts: ["#ef6c00", "#2b6cb0", "#38a169", "#d69e2e", "#718096"],
};

/**
 * 3. SUPER-SLEEK MODERN (Close-CRM aesthetic) — minimal, monochrome, sharp, fast.
 * Near-black vertical rail, crisp indigo accent, tight density, small radius.
 */
export const SLEEK_SKIN: ThemeConfig = {
  id: "sleek",
  name: "Monoline",
  blurb: "Minimal monochrome with a single crisp accent — fast and dense.",
  mode: "light",
  bg: "#fbfbfd",
  surface: "#ffffff",
  surface2: "#f2f3f5",
  fg: "#0b0d12",
  muted: "#6b7280",
  border: "#e7e8ec",
  primary: "#4f46e5",
  primaryFg: "#ffffff",
  ring: "#4f46e5",
  sidebarBg: "#0d0f14",
  sidebarFg: "#e6e8ee",
  radius: "5px",
  font: INTER,
  headerLayout: "vertical",
  density: "compact",
  charts: ["#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899"],
};

/**
 * 4. FRESH NATURAL (Bitrix / amoCRM) — organic greens, airy whitespace, soft nature
 * gradients behind boards, generous rounding.
 */
export const FRESH_SKIN: ThemeConfig = {
  id: "fresh",
  name: "Meadow",
  blurb: "Calm natural greens with soft landscape gradients behind your boards.",
  mode: "light",
  bg: "#f3f7f0",
  surface: "#ffffff",
  surface2: "#e8f0e4",
  fg: "#1c2b21",
  muted: "#5c6b5a",
  border: "#d9e6d2",
  primary: "#2f9e57",
  // Dark ink, not white: white on this fill measures 3.41:1 and a 14px button
  // label needs 4.5:1. Keeps the brand hue and reaches 5.11:1 — caught by the
  // brand contrast validator, which the shipped skins have to pass too.
  primaryFg: "#1a1a1a",
  ring: "#3bb36a",
  sidebarBg: "#ffffff",
  sidebarFg: "#1c2b21",
  radius: "14px",
  font: INTER,
  headerLayout: "horizontal",
  density: "comfortable",
  revealBg: true,
  // A real photographic highland meadow behind the airy white cards, washed with a light
  // scrim so content stays legible over it (fixed = the scene stays put as pages scroll).
  appBg:
    "linear-gradient(rgba(244,248,242,0.50), rgba(236,243,234,0.60))," +
    'url("/whitelabel/meadow.jpg") center / cover no-repeat fixed',
  boardBg:
    "linear-gradient(rgba(244,248,242,0.42), rgba(236,243,234,0.52))," +
    'url("/whitelabel/meadow.jpg") center / cover no-repeat fixed',
  charts: ["#2f9e57", "#4fb0a0", "#8bc34a", "#c9a24b", "#5a8f6b"],
};

/**
 * 5. QOD SIGNATURE — the premium house look. Same QOD navy + sky + gold as the app
 * (no new brand hue), but elevated: soft cool-neutral canvas, generous rounding, and
 * an airy shell gradient. Bespoke-grade polish without leaving the brand's colours.
 */
export const PREMIUM_SKIN: ThemeConfig = {
  id: "premium",
  name: "QOD Signature",
  blurb: "Refined QOD navy — soft cool neutrals, generous rounding, executive polish.",
  mode: "light",
  bg: "#f6f8fb",
  surface: "#ffffff",
  surface2: "#eef2f7",
  fg: "#132133",
  muted: "#5c6b80",
  border: "#e4e9f0",
  primary: "#1f4e79",   // the app's navy — same colour family, not a new hue
  primaryFg: "#ffffff",
  ring: "#1ba1e2",      // the app's sky
  radius: "12px",       // generous but composed (chrome scale ≈ 1.5)
  font: INTER,
  headerLayout: "horizontal",
  density: "comfortable",
  appBg: "linear-gradient(180deg, #eef3f9 0%, #f6f8fb 46%)",
  charts: ["#1f4e79", "#1ba1e2", "#c9a24b", "#2fa36b", "#7c5cd6"],
};

export const SKINS: ThemeConfig[] = [
  DEFAULT_SKIN,
  PREMIUM_SKIN,
  GLASS_SKIN,
  HANDYMAN_SKIN,
  SLEEK_SKIN,
  FRESH_SKIN,
];

export const SKINS_BY_ID: Record<string, ThemeConfig> = Object.fromEntries(
  SKINS.map((s) => [s.id, s]),
);

export function getSkin(id: string | null | undefined): ThemeConfig | null {
  if (!id) return null;
  return SKINS_BY_ID[id] ?? null;
}
